<template>
  <section class="list-filters">
    <div class="filter-row primary-row">
      <v-text-field
        v-model="local.search"
        dense
        flat
        solo
        clearable
        hide-details
        prepend-inner-icon="mdi-magnify"
        placeholder="Buscar"
        class="filter-search"
      />
      <component
        :is="componentFor(field)"
        v-for="field in primaryExtraFields"
        :key="field.name"
        v-model="local[field.name]"
        dense
        flat
        solo
        clearable
        hide-details
        :items="field.items || []"
        :item-text="field.itemText || 'text'"
        :item-value="field.itemValue || 'value'"
        :label="field.label"
        :type="inputTypeFor(field)"
        :prefix="field.prefix || undefined"
        :inputmode="field.type === 'currency' || field.type === 'number' ? 'decimal' : undefined"
        :allow-negative="field.allowNegative !== false"
        :multiple="!!field.multiple"
        :chips="!!field.multiple"
        :small-chips="!!field.multiple"
        :class="['primary-extra-field', fieldClass(field)]"
      />
      <v-select
        v-if="dateRange"
        v-model="local.period"
        :items="periodOptions"
        dense
        flat
        solo
        hide-details
        class="period-filter"
      />
      <v-menu v-if="dateRange" v-model="dateRangeMenu" offset-y left max-width="360" :close-on-content-click="false">
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon class="date-button" :disabled="local.period !== 'custom'" v-bind="attrs" v-on="on">
            <v-icon size="18">mdi-calendar-range-outline</v-icon>
          </v-btn>
        </template>

        <v-card class="filter-card">
          <v-card-title>Intervalo personalizado</v-card-title>
          <v-card-text>
            <v-text-field v-model="local.start_date" label="Data inicial" type="date" outlined dense hide-details="auto" class="mb-3" />
            <v-text-field v-model="local.end_date" label="Data final" type="date" outlined dense hide-details="auto" />
          </v-card-text>
          <v-card-actions>
            <v-btn text @click="clearCustomRange">Limpar</v-btn>
            <v-spacer />
            <v-btn depressed color="primary" @click="dateRangeMenu = false">Aplicar</v-btn>
          </v-card-actions>
        </v-card>
      </v-menu>
      <v-btn
        v-if="hasExtraFilters"
        icon
        class="more-button"
        :title="showMore ? 'Ocultar filtros' : 'Mais filtros'"
        @click="showMore = !showMore"
      >
        <v-icon>{{ showMore ? "mdi-filter-variant-minus" : "mdi-filter-variant-plus" }}</v-icon>
      </v-btn>
      <v-btn v-if="hasFilledFilters" small text class="clear-button" @click="clear">
        Limpar
      </v-btn>
    </div>

    <v-expand-transition>
      <div v-if="hasExtraFilters && showMore" class="filter-row more-row">
        <component
          :is="componentFor(field)"
          v-for="field in extraFields"
          :key="field.name"
          v-model="local[field.name]"
          dense
          flat
          solo
          clearable
          hide-details
          :items="field.items || []"
          :item-text="field.itemText || 'text'"
          :item-value="field.itemValue || 'value'"
          :label="field.label"
          :type="inputTypeFor(field)"
          :prefix="field.prefix || undefined"
          :inputmode="field.type === 'currency' || field.type === 'number' ? 'decimal' : undefined"
          :allow-negative="field.allowNegative !== false"
          :multiple="!!field.multiple"
          :chips="!!field.multiple"
          :small-chips="!!field.multiple"
          :class="['extra-field', fieldClass(field)]"
        />
      </div>
    </v-expand-transition>
  </section>
</template>

<script>
import { initialListFilters } from "@/views/boarua/helpers/listFilters";

export default {
  name: "BoaruaListFilters",
  props: {
    value: {
      type: Object,
      default: () => initialListFilters(),
    },
    fields: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    searchDebounce: {
      type: Number,
      default: 450,
    },
    dateRange: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      local: Object.assign(initialListFilters(), this.value),
      showMore: false,
      dateRangeMenu: false,
      searchTimer: null,
      syncingFromParent: false,
      lastAppliedSearch: "",
      periodOptions: [
        { text: "Todo período", value: "all" },
        { text: "Mês atual", value: "current_month" },
        { text: "Últimos 3 meses", value: "last_3_months" },
        { text: "Últimos 6 meses", value: "last_6_months" },
        { text: "Ano atual", value: "current_year" },
        { text: "Personalizado", value: "custom" },
      ],
    };
  },
  watch: {
    value: {
      deep: true,
      handler(value) {
        const next = Object.assign(initialListFilters(), value || {});
        if (JSON.stringify(next) !== JSON.stringify(this.local)) {
          this.syncingFromParent = true;
          this.local = next;
          this.$nextTick(() => {
            this.syncingFromParent = false;
          });
        }
      },
    },
    local: {
      deep: true,
      handler(value) {
        if (JSON.stringify(value) !== JSON.stringify(this.value || {})) {
          this.$emit("input", Object.assign({}, value));
        }

        if (!this.syncingFromParent) {
          this.scheduleAutoFilter(value);
        }
      },
    },
    "local.period"(period) {
      if (period !== "custom") {
        this.local.start_date = "";
        this.local.end_date = "";
        this.dateRangeMenu = false;
        return;
      }

      if (!this.local.start_date && !this.local.end_date) {
        const now = new Date();
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        this.local.start_date = this.toDateInput(firstDay);
        this.local.end_date = this.toDateInput(now);
      }
      this.dateRangeMenu = true;
    },
  },
  beforeDestroy() {
    this.clearSearchTimer();
  },
  computed: {
    allFields() {
      return (Array.isArray(this.fields) ? this.fields : []).filter(Boolean);
    },
    extraFields() {
      return this.allFields.filter((field) => !field.primary);
    },
    primaryExtraFields() {
      return this.allFields.filter((field) => field.primary);
    },
    hasExtraFilters() {
      return this.extraFields.length > 0;
    },
    hasFilledFilters() {
      const values = this.local || {};
      return Object.keys(values).some((key) => {
        const value = values[key];
        if (key === "period") return this.dateRange && value && value !== "all";
        if (["start_date", "end_date"].includes(key)) return this.dateRange && values.period === "custom" && !!value;
        if (Array.isArray(value)) return value.length > 0;
        return value !== "" && value !== null && value !== undefined;
      });
    },
  },
  methods: {
    componentFor(field) {
      if (field.type === "currency") return "v-currency-field";
      return field.type === "select" ? "v-select" : "v-text-field";
    },
    inputTypeFor(field) {
      if (field.type === "date") return "date";
      if (field.type === "number") return "number";
      return "text";
    },
    fieldClass(field) {
      return {
        "date-field": field.type === "date",
        "money-field": field.type === "currency",
        "text-filter-field": !["date", "currency", "select"].includes(field.type),
      };
    },
    apply() {
      this.clearSearchTimer();
      const payload = this.filterPayload(this.local);
      this.lastAppliedSearch = String(payload.search || "").trim();
      this.$emit("filter", payload);
    },
    clear() {
      this.clearSearchTimer();
      const next = initialListFilters();
      this.allFields.forEach((field) => {
        next[field.name] = field.multiple ? [] : "";
      });
      this.syncingFromParent = true;
      this.local = next;
      this.dateRangeMenu = false;
      this.showMore = false;
      this.$nextTick(() => {
        this.syncingFromParent = false;
      });
      this.lastAppliedSearch = "";
      this.$emit("clear", Object.assign({}, next));
    },
    clearSearchTimer() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
        this.searchTimer = null;
      }
    },
    filterPayload(value) {
      const payload = Object.assign({}, value || {});
      const search = String(payload.search || "").trim();
      payload.search = search.length >= 3 ? search : "";
      payload.period = this.dateRange ? payload.period || "all" : "all";
      if (!this.dateRange || payload.period !== "custom") {
        payload.start_date = "";
        payload.end_date = "";
      }
      return payload;
    },
    hasActiveNonSearchFilters(value) {
      const values = value || {};
      if (this.dateRange && values.period && values.period !== "all") return true;
      return this.allFields.some((field) => {
        const current = values[field.name];
        if (Array.isArray(current)) return current.length > 0;
        return current !== "" && current !== null && current !== undefined;
      });
    },
    shouldAutoApply(value) {
      const search = String((value && value.search) || "").trim();
      if (search.length === 0 || search.length >= 3) return true;
      if (this.hasActiveNonSearchFilters(value)) return true;
      return this.lastAppliedSearch.length >= 3;
    },
    scheduleAutoFilter(value) {
      this.clearSearchTimer();

      if (!this.shouldAutoApply(value)) return;

      this.searchTimer = setTimeout(() => {
        this.searchTimer = null;
        const payload = this.filterPayload(this.local);
        this.lastAppliedSearch = String(payload.search || "").trim();
        this.$emit("filter", payload);
      }, this.searchDebounce);
    },
    clearCustomRange() {
      this.local.start_date = "";
      this.local.end_date = "";
      this.dateRangeMenu = false;
    },
    toDateInput(value) {
      const date = new Date(value);
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    },
  },
};
</script>

<style scoped lang="scss">
.list-filters {
  margin: 0 0 16px;
  padding: 0;
  background: transparent;
}

.filter-row {
  display: flex;
  gap: 10px;
  align-items: center;
}

.primary-row {
  justify-content: flex-end;
}

.filter-row > * {
  min-width: 0;
}

.more-row {
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  background: transparent;
}

.more-row > * {
  flex: 0 1 180px;
}

.more-row .date-field {
  flex-basis: 170px;
}

.more-row .money-field {
  flex-basis: 220px;
}

.more-row .text-filter-field {
  flex-basis: 220px;
}

.filter-search {
  flex: 1 1 260px;
}

.period-filter {
  flex: 0 0 210px;
}

.primary-extra-field {
  flex: 0 1 210px;
}

.filter-search::v-deep,
.period-filter::v-deep,
.primary-extra-field::v-deep,
.more-row > *::v-deep {
  .v-input__control {
    min-height: 40px;
  }

  .v-input__slot {
    min-height: 40px !important;
    background: #ededeb !important;
    border-radius: 18px;
    box-shadow: none !important;
  }

  .v-label,
  input,
  .v-select__selection,
  .v-input__prepend-inner .v-icon,
  .v-input__append-inner .v-icon {
    color: #66736b !important;
    font-size: 13px;
  }
}

.filter-card {
  border-radius: 8px;
}

.date-button,
.more-button {
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  color: #111111;
  background: #ededeb;
  border-radius: 18px;
}

.date-button.v-btn--disabled {
  opacity: 0.45;
}

.clear-button {
  flex: 0 0 auto;
  color: #66736b;
  min-height: 38px;
  font-weight: 600;
}

@media (max-width: 980px) {
  .primary-row {
    flex-wrap: wrap;
    justify-content: flex-start;
  }

  .filter-search {
    flex: 1 1 100%;
  }

  .primary-extra-field,
  .period-filter {
    flex: 1 1 180px;
  }

  .date-button,
  .more-button {
    flex: 0 0 40px;
  }

  .clear-button {
    margin-left: auto;
  }
}

@media (max-width: 900px) {
  .list-filters {
    padding: 0;
  }

  .primary-row {
    flex-wrap: wrap;
  }

  .period-filter {
    flex: 1 1 calc(100% - 100px);
  }

  .primary-extra-field {
    flex: 1 1 100%;
  }

  .more-row {
    gap: 10px;
  }

  .more-row > *,
  .more-row .date-field,
  .more-row .money-field,
  .more-row .text-filter-field {
    flex: 1 1 100%;
  }

  .clear-button {
    flex: 1 1 100%;
    width: 100%;
  }
}
</style>
